import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { KPI_LEVEL_FILTER_OPTIONS } from "../utils/kpi-level-labels";

export function KPIFilter() {
  const nav = useNavigate({ from: "/app/kpis" });
  const search = useSearch({ from: "/app/kpis" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const levelFilter = findCondition(filters, "level");
  const statusFilter = findCondition(filters, "status");
  const departmentFilter = findCondition(filters, "departmentId");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const levelValue = (levelFilter?.value as string) || "all";
  const statusValue = (statusFilter?.value as string) || "all";
  const departmentValue = departmentFilter?.value as string;

  const hasFilter =
    !!nameFilter?.value ||
    levelValue !== "all" ||
    statusValue !== "all" ||
    !!departmentValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="kpi-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ KPI..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={levelValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "level");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "level",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-40"
            options={KPI_LEVEL_FILTER_OPTIONS}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ກຳລັງໃຊ້ງານ" },
              { value: "draft", label: "ຮ່າງ" },
              { value: "inactive", label: "ປິດການໃຊ້ງານ" },
            ]}
          />
          <InfiniteCombobox<{ id: string; name: string }>
            queryKey={["departments", "lookup"]}
            className="h-8 w-full sm:w-48"
            value={departmentValue || ""}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (!val) {
                nextFilters = removeConditions(filters, "departmentId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            clearable
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
